import {
  useGetQSetByTaskId,
  useGetQSetQuestionsByTaskId,
  useRandomPreviewQuestionsByTaskId,
} from '@exam-room/graphql/preview';
import { dayjs } from '@shared/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useExamEvent } from '../exam-event';
import { useExamPreviewSettings } from '../exam-preview-settings';
import { NewExamContext as ExamPreviewProviderContext } from '../new-exam';
import {
  type Exam,
  type ExamPaper,
  type QuestionWithOrder,
  type NewExamContextValue as ExamPreviewContextValue,
} from '../new-exam/types';

import { useGetQuestionsWithImage } from './hooks';
import { MOCK_MODULE_EXAM } from './mockData';
import { generateExamPaper, generateQuestions, generateTags } from './utils';

interface ExamPreviewProviderProps {
  children: React.ReactNode;
}

function ExamPreviewProvider({ children }: Readonly<ExamPreviewProviderProps>) {
  const { taskId } = useParams();
  const { previewSettings } = useExamPreviewSettings();
  const { examEvent } = useExamEvent();
  const { withTimer, shouldSortByExamSettings } = previewSettings;

  const [examTime, setExamTime] = useState<{ startedAt: Date | null; endedAt: Date | null } | null>(
    null,
  );
  const [currentExamOrder] = useState<number | null>(null);

  const isExamLoading = false;
  const isExamPaperLoading = false;

  const setExamEndTime = (time: Date) => {
    setExamTime((prev) => ({
      startedAt: prev?.startedAt ?? null,
      endedAt: time,
    }));
  };

  const {
    data: getQSetByTaskIdData,
    loading: isGetQSetByTaskIdLoading,
    error: getQSetByTaskIdError,
  } = useGetQSetByTaskId({
    variables: {
      input: {
        taskId: taskId as string,
      },
    },
  });

  const qSet = useMemo(() => getQSetByTaskIdData?.excGetQSetByTaskId?.qSet, [getQSetByTaskIdData]);

  const {
    data: qSetQuestionsData,
    loading: isQSetQuestionsLoading,
    error: qSetQuestionsError,
  } = useGetQSetQuestionsByTaskId({
    variables: {
      input: {
        taskId: taskId as string,
      },
    },
    skip: !qSet?.id,
  });

  const {
    data: randomPreviewQuestionsData,
    loading: isRandomPreviewQuestionsLoading,
    error: randomPreviewQuestionsError,
  } = useRandomPreviewQuestionsByTaskId({
    variables: {
      input: {
        taskId: taskId as string,
        options: {
          isRandom: shouldSortByExamSettings,
        },
      },
    },
    skip: !qSet?.id,
  });

  const qConfigStructures = useMemo(() => {
    if (qSet?.metadata?.qConfigStructures) {
      return qSet.metadata.qConfigStructures;
    }

    // Fallback to qConfigStructureParts if qSet doesn't have qConfigStructures
    if (qSet?.metadata?.qConfigStructureParts) {
      return qSet.metadata.qConfigStructureParts;
    }

    return [];
  }, [getQSetByTaskIdData?.excGetQSetByTaskId?.qSet?.metadata]);

  const tags = useMemo(() => generateTags(qConfigStructures), [qConfigStructures]);

  const qSetQuestions = useMemo(
    () => qSetQuestionsData?.excGetQSetQuestionsByTaskId?.qSetQuestions ?? [],
    [qSetQuestionsData],
  );

  const masterQuestions = useMemo(() => {
    return generateQuestions(qSetQuestions, tags);
  }, [tags, qSetQuestions]);

  const randomizedQuestionIds = useMemo(() => {
    return (
      randomPreviewQuestionsData?.excRandomPreviewQuestionsByTaskId?.randomPreviewQuestionIds ?? []
    );
  }, [randomPreviewQuestionsData]);

  const examPaper: ExamPaper = useMemo(() => {
    return generateExamPaper(MOCK_MODULE_EXAM, masterQuestions, randomizedQuestionIds);
  }, [masterQuestions, randomizedQuestionIds]);

  const examPaperItems = examPaper.examPaperItems;

  const questions = useMemo(() => {
    return examPaperItems
      ?.map(({ question }, idx) => {
        if (!question) return null;
        return {
          ...question,
          order: idx + 1,
        };
      })
      .filter((item) => item !== null)
      .sort((a, b) => a.order - b.order);
  }, [examPaperItems]);

  const {
    data: parsedQuestions,
    loading: isGetQuestionsWithImageLoading,
    cacheStatus,
    error: questionsWithImageError,
  } = useGetQuestionsWithImage(questions, qSet?.id, taskId);

  const questionMap = useMemo(() => {
    const map: Record<string, QuestionWithOrder> = {};
    for (const question of parsedQuestions) {
      map[question.id] = question;
    }
    return map;
  }, [parsedQuestions]);

  const refetch = useCallback(async () => {
    // Preview mode doesn't need refetch functionality
  }, []);

  const attachDocumentUrl = examEvent.metadata.attachDocument as null;

  const contextValue: ExamPreviewContextValue = useMemo(() => {
    return {
      exam: MOCK_MODULE_EXAM as Exam,
      examPaper,
      questions: parsedQuestions,
      questionMap,
      loading:
        isExamLoading ||
        isExamPaperLoading ||
        isGetQSetByTaskIdLoading ||
        isQSetQuestionsLoading ||
        isGetQuestionsWithImageLoading ||
        isRandomPreviewQuestionsLoading,
      examTime,
      setExamEndTime,
      currentExamOrder,
      cacheStatus,
      attachDocumentUrl,
      error:
        getQSetByTaskIdError ||
        qSetQuestionsError ||
        questionsWithImageError ||
        randomPreviewQuestionsError,
      refetch,
    };
  }, [
    parsedQuestions,
    questionMap,
    isExamLoading,
    isExamPaperLoading,
    isGetQSetByTaskIdLoading,
    isQSetQuestionsLoading,
    isGetQuestionsWithImageLoading,
    isRandomPreviewQuestionsLoading,
    examTime,
    setExamEndTime,
    currentExamOrder,
    examPaper,
    cacheStatus,
    getQSetByTaskIdError,
    qSetQuestionsError,
    questionsWithImageError,
    randomPreviewQuestionsError,
    refetch,
    attachDocumentUrl,
  ]);

  const totalExamDuration = useMemo(() => {
    return qSet?.qConfig.timeLimit?.total; // in minutes
  }, [qSet?.qConfig.timeLimit?.total]);

  useEffect(() => {
    if (withTimer && totalExamDuration) {
      const now = dayjs();

      setExamTime({
        startedAt: now.toDate(),
        endedAt: withTimer ? dayjs().add(totalExamDuration, 'seconds').toDate() : null,
      });
    }
  }, [withTimer, totalExamDuration]);

  return (
    <ExamPreviewProviderContext.Provider value={contextValue}>
      {children}
    </ExamPreviewProviderContext.Provider>
  );
}

export { ExamPreviewProvider };
